import axios, {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { toast } from "sonner";

// --- Types ---

interface TokenPair {
  access_token: string;
  refresh_token: string;
}

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
  _silent?: boolean;
}

// --- Token helpers ---

const ACCESS_KEY = "prep_access_token";
const REFRESH_KEY = "prep_refresh_token";

export function getToken(): string | null {
  return localStorage.getItem(ACCESS_KEY);
}

function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}

export function setTokens(access: string, refresh: string): void {
  localStorage.setItem(ACCESS_KEY, access);
  localStorage.setItem(REFRESH_KEY, refresh);
}

export function clearTokens(): void {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

// --- Axios instance ---

const apiClient = axios.create({
  baseURL:
    (import.meta.env.VITE_API_URL as string | undefined) ??
    "https://api.preppos.com/api/v1",
});

// --- Refresh mutex ---

let isRefreshing = false;
type RefreshSubscriber = (token: string | null, err?: AxiosError) => void;
let refreshSubscribers: RefreshSubscriber[] = [];

function subscribeTokenRefresh(cb: RefreshSubscriber): void {
  refreshSubscribers.push(cb);
}

function onTokenRefreshed(token: string): void {
  const subs = refreshSubscribers;
  refreshSubscribers = [];
  subs.forEach((cb) => cb(token));
}

// Called when /auth/refresh itself fails. Every queued request must settle —
// otherwise those promises hang until the page navigates to /login.
function onRefreshFailed(err: AxiosError): void {
  const subs = refreshSubscribers;
  refreshSubscribers = [];
  subs.forEach((cb) => cb(null, err));
}

// --- Request interceptor ---

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getToken();
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// --- Response interceptor ---

// Maps the 424 detail message to a provider slug so we can deep-link the
// merchant to the right reconnect tile in /app/settings. Backend sends the
// provider name in plain English at the start of `detail.detail` (e.g.
// "Clover token decryption failed. Re-authenticate via OAuth."). Pre-launch
// audit Section 3.1 — surfaces dead POS credentials as an actionable toast
// instead of a generic 500.
// Defense-in-depth: allowlist the providers we actually deep-link to in
// AppSettings. Anything else (a typoed backend response, a future
// provider name we haven't reviewed, or an attacker-shaped 424 detail)
// returns null so the toast still fires but with the generic copy and
// no /app/settings?reconnect=<x> URL injection.
const _ALLOWED_PROVIDERS = new Set(["clover", "square", "toast", "revel", "google"]);

function _extractFailedProvider(detail: unknown): string | null {
  if (!detail || typeof detail !== "object") return null;
  const d = detail as { detail?: unknown; provider?: unknown };
  if (typeof d.provider === "string" && d.provider) {
    const lower = d.provider.toLowerCase();
    return _ALLOWED_PROVIDERS.has(lower) ? lower : null;
  }
  if (typeof d.detail === "string") {
    const match = d.detail.match(/^(Clover|Square|Toast|Revel|Google)/i);
    if (match) {
      const lower = match[1].toLowerCase();
      return _ALLOWED_PROVIDERS.has(lower) ? lower : null;
    }
  }
  return null;
}

// Throttle 424 toasts so a flurry of failed requests against the same
// stale credential doesn't spam the merchant. 30s window per provider.
const _last424ToastAt: Record<string, number> = {};
const TOAST_424_THROTTLE_MS = 30_000;

function _maybeShow424Toast(provider: string | null): void {
  const key = provider ?? "_unknown";
  const now = Date.now();
  if (now - (_last424ToastAt[key] ?? 0) < TOAST_424_THROTTLE_MS) return;
  _last424ToastAt[key] = now;

  const providerLabel = provider
    ? provider.charAt(0).toUpperCase() + provider.slice(1)
    : "POS";
  toast.error(`${providerLabel} credentials invalid — please reconnect`, {
    description:
      "Your POS connection failed. Reconnecting takes about a minute and restores sync.",
    duration: 10_000,
    action: {
      label: "Reconnect",
      onClick: () => {
        const target = provider
          ? `/app/settings?reconnect=${encodeURIComponent(provider)}`
          : "/app/settings";
        // Use direct navigation rather than react-router so the toast
        // works from any page (including ones that haven't mounted a Router).
        window.location.href = target;
      },
    },
  });
}

apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetryableRequestConfig | undefined;
    const status = error.response?.status;

    // Canceled/aborted requests are NOT failures — React Query cancels
    // in-flight queries on unmount and on focus-refetch races (e.g. switching
    // tabs and back). Toasting these produced the "Connection lost" spam.
    if (error.code === "ERR_CANCELED") {
      return Promise.reject(error);
    }

    // --- Categorised error toasts (skip for 401 which triggers refresh, skip if silent) ---
    // Each toast passes a stable `id` so sonner COLLAPSES duplicates: a backend
    // outage or a burst of parallel queries all failing 500 shows one toast, not
    // a stack of identical ones (only 424 had per-provider throttling before).
    if (status !== 401 && !originalRequest?._silent) {
      if (!navigator.onLine) {
        toast.error("Connection lost. Check your internet.", { id: "net-offline" });
      } else if (!error.response) {
        // Online but no response object: a client-side timeout or a brief
        // backend blip (e.g. the API restarting). Stay quiet — the calling
        // screen's own error/retry state handles it, and the retry logic
        // below still runs. Avoids a scary false "connection lost" toast.
      } else if (status === 403) {
        // 403 is FEATURE_NOT_IN_TIER (utils.py require_feature) and carries
        // upgrade_required: true — the merchant CAN have this, on a higher
        // plan. The old copy ("no access") was a dead end and was the 402
        // message by mistake; the two were swapped.
        toast.error("That feature isn't on your current plan.", { id: "http-403" });
      } else if (status === 424) {
        // POS credential decrypt / token-expired surface. Pre-audit this
        // showed as a generic 500. Toast with a Reconnect CTA + deep-link
        // to /app/settings?reconnect=<provider>. Throttled per-provider.
        const detail = (error.response?.data as { detail?: unknown })?.detail;
        _maybeShow424Toast(_extractFailedProvider(detail));
      } else if (status === 402) {
        // 402 is ACCOUNT_NOT_ACTIVE (utils.py require_active_account) —
        // registered but checkout never completed. This is the paywall
        // message that decides whether they convert, so it must say what to
        // do, not blame their plan.
        toast.error("Finish checkout to start your free trial.", { id: "http-402" });
      } else if (status === 429) {
        toast.warning("Too many requests — please try again in a moment.", { id: "http-429" });
      } else if (status && status >= 500) {
        toast.error("Server error. Please try again later.", { id: "http-5xx" });
      } else if (status && status >= 400) {
        // Let component-level handlers show specific messages;
        // fall back to generic only if not already handled
      }
    }

    if (
      status !== 401 ||
      !originalRequest ||
      originalRequest._retry
    ) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    if (isRefreshing) {
      return new Promise<AxiosResponse>((resolve, reject) => {
        subscribeTokenRefresh((newToken, refreshErr) => {
          if (refreshErr || !newToken) {
            reject(refreshErr ?? error);
            return;
          }
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
          }
          resolve(apiClient(originalRequest));
        });
      });
    }

    isRefreshing = true;

    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      onRefreshFailed(error);
      clearTokens();
      isRefreshing = false;
      window.location.href = "/login";
      return Promise.reject(error);
    }

    try {
      const { data } = await axios.post<TokenPair>(
        `${apiClient.defaults.baseURL}/auth/refresh`,
        { refresh_token: refreshToken }
      );

      setTokens(data.access_token, data.refresh_token);
      onTokenRefreshed(data.access_token);

      if (originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${data.access_token}`;
      }
      return apiClient(originalRequest);
    } catch {
      onRefreshFailed(error);
      clearTokens();
      window.location.href = "/login";
      return Promise.reject(error);
    } finally {
      isRefreshing = false;
    }
  }
);

// ---------------------------------------------------------------------------
// Optimistic concurrency helpers (Audit 4.1/4.2/4.3/4.4 M5/M4)
// ---------------------------------------------------------------------------
//
// Every PATCH on a row that exposes updated_at can opt into If-Match
// optimistic concurrency. Pattern:
//   1. Read row → capture updated_at
//   2. PATCH with `If-Match: <updated_at>`
//   3. On 409 <resource>_stale → reload row, show "modified by another user"
//
// The helpers here keep callers thin: ifMatchHeaders() builds the header
// (ISO timestamps need URL encoding because PostgREST surfaces fractional
// seconds as `+00:00` which isn't a valid ETag character) and
// isStaleConflict() predicates the 409 branch.

export function ifMatchHeaders(updatedAt: string | null | undefined): Record<string, string> {
  if (!updatedAt) return {};
  // Wrap in quotes so it parses as a strong ETag at the gateway layer
  // (Cloudflare/nginx normalisation can otherwise strip the value).
  return { "If-Match": `"${updatedAt}"` };
}

export function isStaleConflict(err: unknown): boolean {
  const ax = err as AxiosError<{ detail?: { error?: string } }>;
  if (ax.response?.status !== 409) return false;
  const code = ax.response?.data?.detail?.error;
  return typeof code === "string" && code.endsWith("_stale");
}

// ---------------------------------------------------------------------------
// Unauthenticated client — Phase 3 public landing API (/api/v1/public/*)
// ---------------------------------------------------------------------------
// A SEPARATE axios instance with NO JWT interceptor (the public endpoints take no
// auth) and NO error-toast interceptor (the landing pages render their own inline
// states). `withCredentials: true` so the anonymous, httponly visit cookie
// (SameSite=None, set by the backend) rides the cross-site opt-in/event calls —
// the FE origin must be in the backend ALLOWED_ORIGINS for this to be accepted.
export const unauthApiClient = axios.create({
  baseURL:
    (import.meta.env.VITE_API_URL as string | undefined) ??
    "https://api.preppos.com/api/v1",
  withCredentials: true,
});

export default apiClient;
